import { PollsViewStatuses } from '../../constans/polls-constants';
import ResponseEntity from '../../store/polls-store/response-entity';

type MappedObject = { [key: string]: string };

export const convertStatusIntoClassName = (status: PollsViewStatuses): string => {
  return status.replace(' ', '-').toLowerCase();
};

export const getKeyByValue = (obj: MappedObject, value: string): string => {
  return Object.keys(obj).find((key: string) => obj[key] === value) || '';
};

export const sortByOrder = (arr: ResponseEntity[]): ResponseEntity[] => {
  return arr.sort((a: ResponseEntity, b: ResponseEntity) => a.order - b.order);
};

const TOTAL_PERCENTAGE = 100;
const MIN_NONZERO_PERCENTAGE = 1;
const STEP = 1;
const NONE = 0;

// Largest-remainder rounding: guarantees every option with at least 1 vote shows >=1%, never
// shows a vote share for an option with 0 votes, and the percentages always sum to exactly 100 —
// plain per-option Math.round/Math.floor can't promise any of that. When two or more options are
// tied on remainder (typically because they have the same vote count), the earlier option in
// list order absorbs the rounding remainder first.
export const calculatePollPercentages = (counts: number[]): number[] => {
  const total = counts.reduce((sum, count) => sum + count, NONE);
  if (!total) return counts.map(() => NONE);

  const raw = counts.map(count => (count / total) * TOTAL_PERCENTAGE);
  const rounded = raw.map((percent, idx) => {
    const value = Math.round(percent);
    return counts[idx] > NONE && value === NONE ? MIN_NONZERO_PERCENTAGE : value;
  });

  const diff = TOTAL_PERCENTAGE - rounded.reduce((sum, value) => sum + value, NONE);
  if (diff === NONE) return rounded;

  // An option with 0 votes must never be picked to absorb a positive remainder — only options
  // that actually received votes are eligible to gain a point.
  const candidates = raw
    .map((percent, idx) => ({ idx, remainder: percent - Math.floor(percent) }))
    .filter(entry => diff < NONE || counts[entry.idx] > NONE)
    .sort((a, b) => diff > NONE ? b.remainder - a.remainder : a.remainder - b.remainder);

  let remaining = Math.abs(diff);
  let cursor = NONE;
  while (remaining > NONE && candidates.length && cursor < candidates.length * TOTAL_PERCENTAGE) {
    const idx = candidates[cursor % candidates.length].idx;
    const minAllowed = counts[idx] > NONE ? MIN_NONZERO_PERCENTAGE : NONE;

    if (diff > NONE) {
      rounded[idx] += STEP;
      remaining -= STEP;
    } else if (rounded[idx] > minAllowed) {
      rounded[idx] -= STEP;
      remaining -= STEP;
    }
    cursor += STEP;
  }

  return rounded;
};

interface FullScreenElement extends HTMLDivElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
}

// @ts-ignore
export const openFullscreen = (elem: FullScreenElement): Promise<unknown> => {
  if (elem.requestFullscreen) {
    return elem.requestFullscreen();
  } else if (elem.webkitRequestFullscreen) {
    return elem.webkitRequestFullscreen();
  } else if (elem.msRequestFullscreen) {
    return elem.msRequestFullscreen();
  }else if (elem.mozRequestFullScreen) {
    return elem.mozRequestFullScreen();
  }
};